/* US tide stations, from the layer cache's bbox feed.
 *
 * ═══ THE SAME FACT, ARRIVING A DIFFERENT WAY ═══
 *
 * `tidestations.svelte.ts` holds 158 Canadian stations shipped with the app: one
 * 1.9 MB file, fetched once, good for a year, works with the radio off. That is
 * the right answer for one cruising ground and the wrong one for a hosted chart
 * — the US list is 3 499 stations, and nobody is downloading a year of
 * predictions for all of them to look at Puget Sound.
 *
 * So this asks for the stations IN VIEW and gets them back with their highs and
 * lows already attached, in one request, cached server-side for everyone looking
 * at the same water. The fan-out to NOAA — a request per station — happens in
 * the layer cache. See `services/layer-cache/src/feeds/noaa-tidestations.ts`.
 *
 * ═══ WHAT IT DELIBERATELY DOES NOT DO ═══
 *
 * It does not interpolate, it does not know about datums, and it does not draw.
 * It produces `TideStation[]` — the shape `unpackTides` produces from the
 * Canadian file — so `tideAt` and `tideStep` and `tideStationsLayer` are shared
 * verbatim between the two. That reuse is the point: the second region cost a
 * feed and this file, and not one line of the layer.
 */

import type { Bbox } from '@gennetay/chart-core/lib/geo';
import type { TideStation } from '@gennetay/chart-core/lib/tidestations';
import { sources } from '@gennetay/chart-core/sources';

interface Reply {
  ok: boolean;
  at: number;
  stale: boolean;
  data: {
    box: Bbox;
    stations: {
      id: string;
      name: string;
      lat: number;
      lon: number;
      events: { t: number; m: number; kind: 'H' | 'L' }[];
    }[];
  };
}

/* Matches the feed's own tenth-of-a-degree key. Moving less than this would ask
 * a question the cache has already answered, so it is pure latency. */
const RESTALE_DEG = 0.1;

/* Below this the view is bigger than the feed will answer for (it refuses boxes
 * over 20°) and the symbols would be unreadable anyway. The layer's own minzoom
 * is 9; asking a zoom earlier keeps the stations there when it turns on. */
const MIN_ZOOM = 8;

const boxMoved = (a: Bbox | null, b: Bbox): boolean =>
  a === null ||
  Math.abs(a.west - b.west) > RESTALE_DEG ||
  Math.abs(a.east - b.east) > RESTALE_DEG ||
  Math.abs(a.south - b.south) > RESTALE_DEG ||
  Math.abs(a.north - b.north) > RESTALE_DEG;

class TideStationsFeed {
  /** Exactly what the shipped Canadian store holds, so everything downstream is
   *  shared. `derived` is false — NOAA publishes these directly. */
  stations = $state<TideStation[]>([]);
  error = $state<string | null>(null);
  loading = $state(false);
  /** True when the answer came from the feed's stale window. */
  stale = $state(false);

  #box: Bbox | null = null;
  #retryAfter = 0;

  get available(): boolean {
    return sources().has('feeds');
  }

  /**
   * Ask for the stations in this box. Cheap to call every tick — it returns
   * immediately unless the view has actually moved.
   */
  async ensure(box: Bbox | null, zoom: number): Promise<void> {
    if (!this.available || box === null || this.loading) return;
    if (zoom < MIN_ZOOM) return;
    if (!boxMoved(this.#box, box)) return;
    if (Date.now() < this.#retryAfter) return;

    this.loading = true;
    try {
      const q =
        `feed/noaa.tidestations?west=${box.west.toFixed(3)}&south=${box.south.toFixed(3)}` +
        `&east=${box.east.toFixed(3)}&north=${box.north.toFixed(3)}`;
      const r = await sources().fetch('feeds', q, { signal: AbortSignal.timeout(30_000) });
      if (!r.ok) {
        /* 400 is the chart's fault — zoomed out past what the feed answers for —
         * and 404 means this water is outside US coverage. Neither is a fault
         * worth a red row; the layer simply has nothing here. */
        this.error =
          r.status === 400 || r.status === 404 ? null : `the tide feed answered ${r.status}`;
        this.stations = [];
        this.#retryAfter = Date.now() + 60_000;
        return;
      }
      const j = (await r.json()) as Reply;
      this.stations = (j.data?.stations ?? []).map((s) => ({
        code: s.id,
        name: s.name,
        lat: s.lat,
        lon: s.lon,
        derived: false,
        /* Already sorted by NOAA, and `tideAt` binary-searches them — so sort
         * anyway rather than trust it. A mis-ordered list does not throw, it
         * silently reads the wrong cycle. */
        events: [...s.events].sort((a, b) => a.t - b.t)
      }));
      this.stale = j.stale === true;
      this.error = null;
      this.#box = box;
      this.#retryAfter = 0;
    } catch {
      this.error = 'the tide feed is unreachable';
      this.#retryAfter = Date.now() + 60_000;
    } finally {
      this.loading = false;
    }
  }
}

export const tideStationsFeed = new TideStationsFeed();
